import dayjs, { Dayjs } from 'dayjs';
import PropTypes from 'prop-types';
import qs from 'qs';
import { createContext, useEffect, useState, useMemo, useContext } from 'react';
import type { MeetingsDatedQuery } from '../generated/graphql';
import axios from 'axios';

export type ControllerLoadStatus = 'idle' | 'loading' | 'ok' | 'error' | 'empty';

export interface AppStateContextInterface {
  currentTime: Dayjs;
  appParams: AppParamsT;
  fourWaySplit: boolean;
  additionalWinPriceColumn: string;
  controllerState: string;
  /** Set when GetControllerById has been attempted: 'ok' = got controller, 'empty' = 200 but no controller, 'error' = request failed */
  controllerLoadStatus: ControllerLoadStatus;
}

export function matchDarwinTimestamp(ts: Dayjs = dayjs()) {
  const darwinConvCurrent = ts.tz('Australia/Darwin');

  const adjusted = darwinConvCurrent.isBefore(
    dayjs().tz('Australia/Darwin').second(0).minute(0).hour(5).millisecond(0)
  )
    ? darwinConvCurrent.subtract(1, 'day')
    : darwinConvCurrent;

  return adjusted.second(0).minute(0).hour(5).millisecond(0);
}

export const AppStateContext = createContext<AppStateContextInterface>({
  currentTime: dayjs(),
  appParams: {
    computerName: '',
    screenNumber: 0,
  },
  fourWaySplit: false,
  additionalWinPriceColumn: '',
  controllerState: '',
  controllerLoadStatus: 'idle'
});

interface Props {
  children: React.ReactNode;
}

type NTJRaceMeetingDetail = {
  meeting: Pick<
    Exclude<MeetingsDatedQuery['meetingsDated'][number], null | undefined>,
    'id' | 'track' | 'type'
  >;
};

export type NTJRace = NTJRaceMeetingDetail &
  NonNullable<
    Exclude<MeetingsDatedQuery['meetingsDated'][number], null | undefined>
  >['races'][number];

type AppParamsT = {
  computerName: string;
  screenNumber: number;
};

export function AppStateProvider({ children }: Props) {
  const [currentTime, setCurrentTime] = useState(dayjs());

  const [fourWaySplit, setFourWaySplit] = useState(false);
  const [additionalWinPriceColumn, setAdditionalWinPriceColumn] = useState('');

  const [appParams, setAppParams] = useState<AppParamsT>(() => {
    const params = qs.parse(typeof window !== 'undefined' ? window.location.search : '', {
      ignoreQueryPrefix: true,
    });
    if (params?.screenNumber && params?.computerName) {
      return {
        computerName: String(params.computerName).trim(),
        screenNumber: Number(params.screenNumber) || 0,
      };
    }
    return { computerName: '', screenNumber: 0 };
  });

  const [controllerState, setControllerState] = useState('');
  const [controllerLoadStatus, setControllerLoadStatus] = useState<ControllerLoadStatus>('idle');

  useEffect(() => {
    const params = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    });
    if (params?.screenNumber && params?.computerName) {
      const next = {
        computerName: String(params.computerName).trim(),
        screenNumber: Number(params.screenNumber) || 0,
      };
      setAppParams(next);
    }
  }, []);

  useEffect(() => {
    const controllerId = appParams.computerName?.trim();
    const screenNumber = appParams.screenNumber;
    if (!controllerId || !screenNumber) {
      setControllerLoadStatus('idle');
      return;
    }

    setControllerLoadStatus('loading');
    const c = new AbortController();
    (async () => {
      try {
        const data = await axios.post(
          `${process.env.REACT_APP_LFI_API}/srv.lfi.LFIService/GetControllerById`,
          { controllerId },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Key ${process.env.REACT_APP_LFI_KEY}`,
            },
            signal: c.signal,
          }
        );

        const controller = data?.data?.controller;
        const state = controller?.state ?? '';

        if (!controller) {
          setControllerLoadStatus('empty');
          // eslint-disable-next-line no-console
          console.warn('[LFI] GetControllerById: no controller in response (check channels assigned)', { controllerId, response: data?.data });
        } else {
          setControllerLoadStatus('ok');
        }

        setFourWaySplit(!!controller?.fourWaySplit);
        setAdditionalWinPriceColumn(controller?.additionalWinPriceCol ?? '');
        setControllerState(state);
      } catch (error) {
        if (axios.isCancel(error)) {
          return;
        }
        setControllerLoadStatus('error');
        // eslint-disable-next-line no-console
        console.error('[LFI] GetControllerById failed', error);
      }
    })();

    return () => c.abort();
  }, [appParams.computerName, appParams.screenNumber]);

  useEffect(() => {
    function updateTime() {
      const now = dayjs();
      setCurrentTime(now);
    }

    const timer = window.setInterval(updateTime, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [currentTime]);

  const memorizedValues = useMemo(
    () => ({
      currentTime,
      appParams,
      fourWaySplit,
      additionalWinPriceColumn,
      controllerState,
      controllerLoadStatus
    }),
    [appParams, currentTime, fourWaySplit, additionalWinPriceColumn, controllerState, controllerLoadStatus]
  );

  return (
    <AppStateContext.Provider
      value={memorizedValues}
    >
      {children}
    </AppStateContext.Provider>
  );
}

AppStateProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default function useAppState() {
  return useContext(AppStateContext);
}
